import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getJobs } from './utils/jobApiCache';

// Function to generate slug from job title
const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

// State abbreviation to full name mapping
const stateAbbreviations = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
  'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
  'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
  'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
  'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
  'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
  'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
  'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming',
  'DC': 'District of Columbia'
};

// Function to extract state from title - looks for any 2 capital letters that match a state code
const extractStateFromTitle = (title) => {
  // Find all 2-letter uppercase sequences in the title
  const matches = title.match(/\b([A-Z]{2})\b/g);

  if (matches) {
    // Check each match to see if it's a valid state abbreviation
    for (const abbrev of matches) {
      if (stateAbbreviations[abbrev]) {
        return stateAbbreviations[abbrev];
      }
    }
  }
  return null;
};

// Function to transform API job data to component format
const transformJobData = (apiJob) => {
  let state = null;
  let city = null;
  let country = "United States";

  // First priority: Check job_listing_state and job_listing_city
  if (apiJob.job_listing_state) {
    state = apiJob.job_listing_state;
  }
  if (apiJob.job_listing_city) {
    city = apiJob.job_listing_city;
  }

  // If state/city not found, try to extract state code from title
  if (!state) {
    state = extractStateFromTitle(apiJob.title);
  }

  // If title extraction didn't work, use job_listing_region
  if (!state && apiJob.job_listing_region) {
    if (apiJob.job_listing_region !== "United States") {
      // Use region as state (e.g., "Alaska", "Virginia", "Nevada", etc.)
      state = apiJob.job_listing_region;
    }
  }

  // Default to "United States" if no state found
  if (!state) {
    state = "United States";
  }

  // Normalize job type
  let jobType = apiJob.job_listing_type || "Full Time";
  if (jobType.toLowerCase().includes("part")) {
    jobType = "Part Time";
  } else if (jobType.toLowerCase().includes("full")) {
    jobType = "Full Time";
  } else if (jobType.toLowerCase().includes("contract")) {
    jobType = "Contract";
  } else if (jobType.toLowerCase().includes("intern")) {
    jobType = "Internship";
  }

  // Extract category from job_listing_category
  let category = apiJob.job_listing_category || "Operations";
  // Remove " - CRLLC" suffix if present
  category = category.replace(/\s*-\s*CRLLC\s*$/, "").trim();

  // Build apply URL
  let applyUrl = apiJob.apply_link;
  if (!applyUrl && apiJob.zoho_job_id) {
    applyUrl = `https://hrms.credentia.com/job-form/new?job_title=${apiJob.zoho_job_id}`;
  }
  // If apply_link is an email, convert to mailto link
  if (applyUrl && applyUrl.includes('@') && !applyUrl.startsWith('mailto:') && !applyUrl.startsWith('http')) {
    applyUrl = `mailto:${applyUrl}`;
  }
  if (!applyUrl) {
    applyUrl = "#";
  }

  return {
    id: apiJob.id,
    title: apiJob.title,
    slug: apiJob.slug,
    company: apiJob.job_listing_category || "Operations - CRLLC",
    location: {
      country: country,
      state: state,
      city: city
    },
    jobType: jobType,
    category: category,
    applyUrl: applyUrl,
    content: apiJob.content,
    zoho_job_id: apiJob.zoho_job_id
  };
};

// Export generateSlug for use in other components
export { generateSlug };

// Filter options
const categories = ["Client Relations", "Finance & Accounting", "Human Resources", "Information Technology", "Marketing", "Nurse Aide Evaluation", "Office/Administrative Support", "Operations", "Program/Product", "Sales", "Training & Development"];

const jobTypes = ["Full Time", "Part Time", "Contract", "Internship"];

const locations = {
  "United States": ["Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming", "District of Columbia"]
};

function Career() {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedJobTypes, setSelectedJobTypes] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [openFilter, setOpenFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsData, setJobsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const jobsPerPage = 10;

  // Fetch jobs from API (with caching)
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        setError(null);

        const apiData = await getJobs();

        if (apiData && Array.isArray(apiData)) {
          const transformedJobs = apiData.map(transformJobData);
          setJobsData(transformedJobs);
        } else {
          throw new Error('Invalid API response format');
        }
      } catch (err) {
        console.error('Error fetching jobs:', err);
        setError(err.message);
        setJobsData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  // Extract unique categories and job types from fetched jobs
  const availableCategories = useMemo(() => {
    const uniqueCategories = [...new Set(jobsData.map(job => job.category).filter(Boolean))];
    return uniqueCategories.length > 0 ? uniqueCategories : categories;
  }, [jobsData]);

  const availableJobTypes = useMemo(() => {
    const uniqueJobTypes = [...new Set(jobsData.map(job => job.jobType).filter(Boolean))];
    return uniqueJobTypes.length > 0 ? uniqueJobTypes : jobTypes;
  }, [jobsData]);

  // Extract unique locations from jobs
  const availableLocations = useMemo(() => {
    const locationMap = {};
    jobsData.forEach(job => {
      const country = job.location?.country || "United States";
      const state = job.location?.state;
      if (!locationMap[country]) {
        locationMap[country] = new Set();
      }
      // Only add state if it exists and is not "United States"
      if (state && state !== "United States") {
        locationMap[country].add(state);
      }
    });

    // Convert Sets to Arrays and filter out "United States" from states
    const result = {};
    Object.keys(locationMap).forEach(country => {
      const statesArray = Array.from(locationMap[country])
        .filter(state => state !== "United States")
        .sort();
      result[country] = statesArray;
    });
    return Object.keys(result).length > 0 ? result : locations;
  }, [jobsData]);

  // Filter jobs based on selected filters
  const filteredJobs = useMemo(() => {
    return jobsData.filter(job => {
      const categoryMatch = selectedCategories.length === 0 || selectedCategories.includes(job.category);
      const jobTypeMatch = selectedJobTypes.length === 0 || selectedJobTypes.includes(job.jobType);

      // Location match: if country is selected, include all jobs from that country
      // If state is selected, include jobs from that state
      let locationMatch = selectedLocations.length === 0;
      if (selectedLocations.length > 0) {
        const selectedCountries = selectedLocations.filter(loc =>
          Object.keys(availableLocations).includes(loc)
        );
        const selectedStates = selectedLocations.filter(loc =>
          !Object.keys(availableLocations).includes(loc)
        );

        locationMatch =
          selectedCountries.includes(job.location?.country) ||
          selectedStates.includes(job.location?.state) ||
          (selectedCountries.length === 0 && selectedStates.length === 0);
      }

      return categoryMatch && jobTypeMatch && locationMatch;
    });
  }, [selectedCategories, selectedJobTypes, selectedLocations, jobsData, availableLocations]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategories, selectedJobTypes, selectedLocations]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);
  const startIndex = (currentPage - 1) * jobsPerPage;
  const endIndex = startIndex + jobsPerPage;
  const paginatedJobs = filteredJobs.slice(startIndex, endIndex);

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Toggle filter selection
  const toggleCategory = (category) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const toggleJobType = (jobType) => {
    setSelectedJobTypes(prev =>
      prev.includes(jobType)
        ? prev.filter(j => j !== jobType)
        : [...prev, jobType]
    );
  };

  const toggleLocation = (location) => {
    setSelectedLocations(prev =>
      prev.includes(location)
        ? prev.filter(l => l !== location)
        : [...prev, location]
    );
  };

  // Generate filter summary text
  const getFilterSummary = () => {
    const jobCount = filteredJobs.length;
    const categoryText = selectedCategories.length === 0
      ? "all Categories"
      : selectedCategories.length === 1
        ? "1 Category"
        : `${selectedCategories.length} Categories`;

    const jobTypeText = selectedJobTypes.length === 0
      ? "all Job types"
      : selectedJobTypes.length === 1
        ? "1 Job type"
        : `${selectedJobTypes.length} Job types`;

    const locationText = selectedLocations.length === 0
      ? "all Locations"
      : selectedLocations.length === 1
        ? "1 Location"
        : `${selectedLocations.length} Locations`;

    return `${jobCount} job${jobCount !== 1 ? 's' : ''} in ${categoryText}, ${jobTypeText}, and ${locationText}`;
  };

  return (
    <div className="min-h-screen bg-white p-2 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Main Heading */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-dark-blue mb-12 sm:mb-16 md:mb-24 lg:mb-32 mt-8 sm:mt-12 md:mt-16 lg:mt-20 text-center px-4">
          Unlock Your Full Potential with <span className="text-primary-blue">Credentia</span>
        </h1>

        {/* Filter Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 md:gap-8 mb-8 sm:mb-12 md:mb-20 px-4 items-center sm:items-stretch">
          {/* Categories Filter */}
          <div className="relative flex-1 w-full sm:w-auto max-w-xs sm:max-w-none">
            <button
              onClick={() => setOpenFilter(openFilter === 'categories' ? null : 'categories')}
              className="bg-primary-blue text-white px-4 sm:px-6 md:px-8 py-3 sm:py-4 md:py-5 rounded-full font-semibold hover:opacity-90 transition-opacity text-base sm:text-lg md:text-xl flex items-center justify-between w-full"
            >
              <span>Categories</span>
              <svg
                className={`w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 transition-transform ${openFilter === 'categories' ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {openFilter === 'categories' && (
              <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-10 w-full">
                {availableCategories.map(category => (
                  <label key={category} className="flex items-center gap-2 py-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(category)}
                      onChange={() => toggleCategory(category)}
                      className="w-4 h-4 text-primary-blue border-gray-300 rounded focus:ring-primary-blue"
                    />
                    <span className="text-gray-700">{category}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Job Type Filter */}
          <div className="relative flex-1 w-full sm:w-auto max-w-xs sm:max-w-none">
            <button
              onClick={() => setOpenFilter(openFilter === 'jobTypes' ? null : 'jobTypes')}
              className="bg-primary-blue text-white px-4 sm:px-6 md:px-8 py-3 sm:py-4 md:py-5 rounded-full font-semibold hover:opacity-90 transition-opacity text-base sm:text-lg md:text-xl flex items-center justify-between w-full"
            >
              <span>Job Type</span>
              <svg
                className={`w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 transition-transform ${openFilter === 'jobTypes' ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {openFilter === 'jobTypes' && (
              <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-10 w-full">
                {availableJobTypes.map(jobType => (
                  <label key={jobType} className="flex items-center gap-2 py-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedJobTypes.includes(jobType)}
                      onChange={() => toggleJobType(jobType)}
                      className="w-4 h-4 text-primary-blue border-gray-300 rounded focus:ring-primary-blue"
                    />
                    <span className="text-gray-700">{jobType}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Location Filter */}
          <div className="relative flex-1 w-full sm:w-auto max-w-xs sm:max-w-none">
            <button
              onClick={() => setOpenFilter(openFilter === 'locations' ? null : 'locations')}
              className="bg-primary-blue text-white px-4 sm:px-6 md:px-8 py-3 sm:py-4 md:py-5 rounded-full font-semibold hover:opacity-90 transition-opacity text-base sm:text-lg md:text-xl flex items-center justify-between w-full"
            >
              <span>Location</span>
              <svg
                className={`w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 transition-transform ${openFilter === 'locations' ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {openFilter === 'locations' && (
              <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-10 w-full max-h-[400px] overflow-y-auto">
                {Object.entries(availableLocations).map(([country, states]) => (
                  <div key={country} className="mb-4">
                    <label className="flex items-center gap-2 py-2 cursor-pointer font-semibold text-gray-800">
                      <input
                        type="checkbox"
                        checked={selectedLocations.includes(country)}
                        onChange={() => toggleLocation(country)}
                        className="w-4 h-4 text-primary-blue border-gray-300 rounded focus:ring-primary-blue"
                      />
                      <span>{country}</span>
                    </label>
                    <div className="pl-6 mt-1">
                      {states.map(state => (
                        <label key={state} className="flex items-center gap-2 py-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedLocations.includes(state)}
                            onChange={() => toggleLocation(state)}
                            className="w-4 h-4 text-primary-blue border-gray-300 rounded focus:ring-primary-blue"
                          />
                          <span className="text-gray-700">{state}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Filter Summary Text */}
        {!loading && !error && (
          <div className="mb-8 sm:mb-12 md:mb-16 px-4">
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-gray-800">
              {getFilterSummary()}
            </p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">Loading jobs...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-12">
            <p className="text-red-500 text-lg mb-4">Error loading jobs: {error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-primary-blue text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity"
            >
              Retry
            </button>
          </div>
        )}

        {/* Job Listings */}
        {!loading && !error && (
          <div className="space-y-4 ">
            {paginatedJobs.map(job => (
              <Link
                key={job.id}
                to={`/${job.slug}`}
                className="block bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
                  <div className="flex-1 text-left">
                    <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-primary-blue mb-3 sm:mb-4 md:mb-6 text-left">
                      {job.title}
                    </h3>
                    <div className="inline-block bg-gray-100 text-gray-600 px-3 py-1 rounded-md text-xs sm:text-sm text-left">
                      {job.category}
                    </div>
                  </div>
                  <div className="text-left sm:text-right flex flex-col justify-between sm:justify-between sm:ml-4 sm:self-stretch gap-2 sm:gap-0">
                    <div className='flex items-center mr-2 sm:justify-end sm:mr-0'>
                      <img
                        src="/distance.svg"
                        alt="Distance"
                        className="w-5 h-5 mr-2 sm:hidden"
                      />
                      <span className="text-dark-blue text-sm sm:text-base">
                        {job.location?.city && job.location?.state && job.location.state !== "United States"
                          ? `${job.location.state} - ${job.location.city}`
                          : job.location?.state && job.location.state !== "United States"
                            ? job.location.state
                            : job.location?.country || 'N/A'}
                      </span>
                    </div>
                    <div className='flex sm:justify-end items-center mr-2 sm:mr-0'>
                      <img
                        src="/assignment_turned_in.svg"
                        alt="Distance"
                        className="w-5 h-5 mr-2 sm:hidden"
                      />
                      <span className="text-dark-blue text-sm sm:text-base">{job.jobType}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {!loading && !error && filteredJobs.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No jobs found matching your filters.</p>
          </div>
        )}

        {/* Pagination */}
        {filteredJobs.length > 0 && totalPages > 1 && (
          <div className="flex flex-wrap justify-center items-center gap-2 my-8 px-4">
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium transition-opacity ${currentPage === 1
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-primary-blue text-white hover:opacity-90'
                }`}
            >
              Previous
            </button>

            <div className="flex gap-1 sm:gap-2 flex-wrap justify-center">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium transition-opacity ${currentPage === page
                      ? 'bg-primary-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium transition-opacity ${currentPage === totalPages
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-primary-blue text-white hover:opacity-90'
                }`}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Click outside to close dropdown */}
      {openFilter && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setOpenFilter(null)}
        />
      )}
    </div>
  );
}

export default Career;

